import React from 'react'
import { IMPORT_FAILED_TEXT } from 'src/utils/importRefusal'
import { Box, CircularProgress, LinearProgress, Typography } from '@material-ui/core'
import {
  CheckCircleOutline as StartedIcon,
  HelpOutline as UnknownIcon,
  WarningAmberRounded as WarningIcon,
  RadioButtonUnchecked as QueuedIcon,
} from '@material-ui/icons'
import FileNameText from 'src/components/FileNameText'
import { COHORT_RECOMMENDED, START_ERRORS } from 'src/services/analysisStart'
import { GroupLabel, MONO_SX, Muted } from '../components/ui'
import { formatCredits } from '../model/pricing'
import { PriceSection } from '../ReviewView'
import { carrierSlotLabels } from './model'

// The review before a group run starts, and its progress, in the same centred
// 880px column and status look as the batch review (ReviewView / StartView).

const Section = ({ label, children, sx }) => (
  <Box sx={{ mt: 4, ...sx }}>
    {label && <GroupLabel>{label}</GroupLabel>}
    {children}
  </Box>
)

const Heading = ({ title, sentence }) => (
  <>
    <Typography sx={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</Typography>
    {sentence && <Typography sx={{ fontSize: 15, color: 'text.secondary', mt: 1 }}>{sentence}</Typography>}
  </>
)

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

export const groupReviewHeading = (kind, count) => {
  if (kind === 'multisample') return `Start ${plural(count, 'multisample analysis', 'multisample analyses')}`
  if (kind === 'cohort') return 'Build a CNV cohort'
  if (kind === 'carrier') return `Start ${plural(count, 'carrier analysis', 'carrier analyses')}`
  return ''
}

const MultisampleReview = ({ groups }) => (
  <>
    <Heading
      title={groupReviewHeading('multisample', groups.length)}
      sentence="One analysis per group. Finds variants shared or excluded across finished analyses."
    />
    {groups.some((g) => !(g.name || '').trim()) && (
      <Muted sx={{ mt: 1 }}>Unnamed groups are named by their start time.</Muted>
    )}
    {groups.map((group) => (
      <Section key={group.uid} label={(group.name || '').trim() || 'Unnamed group'}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5 }}>
          {group.members.map((m) => (
            <Typography key={m.uid} component="span" sx={{ ...MONO_SX }}>
              {m.key}
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {` (${(m.relation || '').trim()}${m.include ? '' : ', excluded'}${
                  m.include && m.gt && m.gt !== 'any' ? `, ${m.gt}` : ''
                })`}
              </Box>
            </Typography>
          ))}
        </Box>
      </Section>
    ))}
    <Section label="Price">
      <Muted>No charge.</Muted>
    </Section>
  </>
)

export const cohortSentence = ({ methodLabel, name, kitName, count }) =>
  `${methodLabel} cohort ${name.trim()} on ${kitName}, from ${plural(count, 'analysis', 'analyses')}.`

const CohortReview = ({ methodLabel, name, kitName, members }) => (
  <>
    <Heading
      title={groupReviewHeading('cohort')}
      sentence={cohortSentence({ methodLabel, name, kitName, count: members.length })}
    />
    {members.length > COHORT_RECOMMENDED && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
        <WarningIcon sx={{ fontSize: 17, color: 'text.primary' }} />
        <Typography sx={{ fontSize: 14 }}>More than {COHORT_RECOMMENDED} samples. The build takes longer.</Typography>
      </Box>
    )}
    <Section label={`Samples (${members.length})`}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          columnGap: 3,
          rowGap: 0.5,
        }}
      >
        {members.map((m) => (
          <FileNameText key={m.pairId} name={m.key} variant="plain" maxLines={1} sx={{ fontSize: 13.5 }} />
        ))}
      </Box>
    </Section>
    <Section label="Price">
      <Muted>No charge.</Muted>
    </Section>
  </>
)

// price: groupPrice('carrier', ...) from model/pricing, one charge per carrier analysis.
const CarrierReview = ({ groups, configTitle, samplesRequired, price, balance }) => {
  const labels = carrierSlotLabels(samplesRequired)
  const noun = labels.length === 2 ? 'Couple' : 'Group'
  return (
    <>
      <Heading title={groupReviewHeading('carrier', groups.length)} sentence={`Carrier · ${configTitle}`} />
      {groups.map((group, index) => (
        <Section key={group.uid} label={`${noun} ${index + 1}`}>
          {group.members.map((m, slot) => (
            <Box key={labels[slot]} sx={{ display: 'flex', gap: 2, minHeight: 26, alignItems: 'baseline' }}>
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary', width: 90, flexShrink: 0 }}>
                {labels[slot]}
              </Typography>
              <Typography sx={{ ...MONO_SX }}>{m?.key}</Typography>
            </Box>
          ))}
        </Section>
      ))}
      <Section label="Price">
        <PriceSection price={price} balance={balance} count={groups.length} noun={['analysis', 'analyses']} />
      </Section>
    </>
  )
}

/** The review of a group run. `kind`: 'multisample' | 'cohort' | 'carrier'. */
export const GroupReviewView = (props) => (
  <Box sx={{ maxWidth: 880, mx: 'auto', py: 2 }}>
    {props.kind === 'multisample' && <MultisampleReview groups={props.groups} />}
    {props.kind === 'cohort' && (
      <CohortReview methodLabel={props.methodLabel} name={props.name} kitName={props.kitName} members={props.members} />
    )}
    {props.kind === 'carrier' && (
      <CarrierReview
        groups={props.groups}
        configTitle={props.configTitle}
        samplesRequired={props.samplesRequired}
        price={props.price}
        balance={props.balance}
      />
    )}
  </Box>
)

// ----------------------------------------------------------------------

const StatusCell = ({ state }) => {
  const status = state?.status || 'queued'
  if (status === 'started') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <StartedIcon sx={{ fontSize: 17, color: 'success.main' }} />
        <Typography sx={{ fontSize: 14 }}>Started</Typography>
      </Box>
    )
  }
  if (status === 'starting') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CircularProgress size={14} thickness={5} />
        <Typography sx={{ fontSize: 14 }}>Starting</Typography>
      </Box>
    )
  }
  if (status === 'unknown') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: 'text.secondary' }}>
        <UnknownIcon sx={{ fontSize: 17, mt: '2px' }} />
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          {state.detail?.error || START_ERRORS.no_reply}
        </Typography>
      </Box>
    )
  }
  // A clean refusal (for example a panel whose chemistry cannot be analysed): nothing
  // was written or charged, so it reads as a plain note with the server's reason.
  if (status === 'failed' && state.detail?.code === 'refused' && !state.detail?.importRefused) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: 'text.secondary' }}>
        <UnknownIcon sx={{ fontSize: 17, mt: '2px' }} />
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }} data-testid="start-refused">
          Not started: {state.detail.error}
        </Typography>
      </Box>
    )
  }
  // A file the backend could not import: the one neutral sentence and nothing more.
  if (status === 'failed' && state.detail?.importRefused) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, color: 'text.secondary' }}>
        <UnknownIcon sx={{ fontSize: 17, mt: '2px' }} />
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }} data-testid="start-import-refused">
          {IMPORT_FAILED_TEXT}
        </Typography>
      </Box>
    )
  }
  if (status === 'failed' || status === 'skipped') {
    const detail = state.detail || {}
    let reason = detail.error || 'Not started'
    if (detail.code === 'insufficient_balance' && detail.neededBalance) {
      reason = `Not enough balance, needs ${formatCredits(Number(detail.neededBalance))} more`
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <WarningIcon sx={{ fontSize: 17, color: 'error.main' }} />
        <Typography sx={{ fontSize: 14 }} noWrap title={reason}>
          Not started: {reason.charAt(0).toLowerCase() + reason.slice(1)}
        </Typography>
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
      <QueuedIcon sx={{ fontSize: 16 }} />
      <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Queued to start</Typography>
    </Box>
  )
}

export const groupRunHeading = (run) => {
  const states = run.order.map((key) => run.status[key])
  const started = states.filter((s) => s?.status === 'started').length
  const notStarted = states.filter((s) => s?.status === 'failed' || s?.status === 'skipped').length
  const noReply = states.filter((s) => s?.status === 'unknown').length
  const settled = started + notStarted + noReply
  const total = run.order.length
  if (run.kind === 'cohort') {
    if (run.running) return 'Starting the cohort build'
    if (started) return `Cohort ${run.name} is building`
    if (noReply) return 'No reply from the server'
    return 'The cohort did not start'
  }
  if (run.running) return `Starting ${Math.min(settled + 1, total)} of ${total}`
  if (notStarted === 0 && noReply === 0) return `${started} ${started === 1 ? 'analysis' : 'analyses'} started`
  return [`${started} started`, notStarted && `${notStarted} not started`, noReply && `${noReply} no reply`]
    .filter(Boolean)
    .join('. ')
}

const runSentence = (run) => {
  if (run.running) return 'Keep this tab open until every start is sent. They start one at a time.'
  if (run.kind === 'cohort') {
    const started = run.order.some((key) => run.status[key]?.status === 'started')
    return started ? 'It shows in the CNV cohort list for this kit when done.' : ''
  }
  if (run.kind === 'carrier' && run.batch?.name) return `Batch ${run.batch.name}.`
  return ''
}

/** Progress and outcome of a group run: run = { kind, order, keys, status, running, batch?, name? }. */
export const GroupStartView = ({ run }) => {
  const total = run.order.length
  const settled = run.order.filter((key) =>
    ['started', 'failed', 'skipped', 'unknown'].includes(run.status[key]?.status),
  ).length
  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', py: 2 }}>
      <Heading title={groupRunHeading(run)} sentence={runSentence(run)} />
      <LinearProgress
        variant="determinate"
        value={total ? (settled / total) * 100 : 0}
        sx={{ mt: 3, height: 6, borderRadius: 3, maxWidth: 480 }}
      />
      <Box sx={{ mt: 4 }}>
        {run.order.map((key) => (
          <Box
            key={key}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 280px) minmax(0, 1fr)',
              columnGap: 3,
              alignItems: 'center',
              minHeight: 36,
            }}
          >
            <FileNameText name={run.keys[key]} variant="plain" maxLines={1} sx={{ fontSize: 13.5 }} />
            <StatusCell state={run.status[key]} />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default GroupReviewView
